// frontend/src/pages/Login.js

import React, { useMemo, useState, useEffect } from "react";            // ← added useEffect
import { Link, useNavigate, useLocation } from "react-router-dom";      // ← added useLocation
import { motion } from "framer-motion";
import axios from "../axiosConfig";

const DECOR_IMAGES = [
  { src: "/images/bitcoin.png",         style: "top-4 left-4 w-12 opacity-20 hidden sm:block" },
  { src: "/images/ethereum.png",        style: "top-4 right-4 w-12 opacity-20 hidden sm:block" },
  { src: "/images/usdt.png",            style: "bottom-4 left-6 w-16 opacity-20 hidden sm:block" },
  { src: "/images/security-shield.png", style: "bottom-4 right-6 w-16 opacity-20 hidden sm:block" },
];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();                                          // ← added

  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [message, setMessage]         = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [showPassword, setShowPass]   = useState(false);

  const emailTrimmed = useMemo(() => email.trim(), [email]);

  // ← NEW: show any backend redirect messages (?msg=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const m = params.get("msg");
    if (m) setMessage(m);
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setMessage("");
    setSubmitting(true);

    try {
      const res = await axios.post("/login", { email: emailTrimmed, password });
      localStorage.setItem("verifyEmail", emailTrimmed);
      setMessage(res.data?.message || "OTP sent to your email.");
      navigate("/verify-login-otp");
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-blue-50 dark:bg-slate-900 px-3 sm:px-4 py-6">
      {/* Autofill + dark-mode legibility fixes */}
      <style>{`
        /* Force good contrast in dark mode for autofill */
        .dark input:-webkit-autofill,
        .dark input:-webkit-autofill:focus,
        .dark input:-webkit-autofill:hover {
          -webkit-text-fill-color: #e5e7eb; /* slate-200 */
          transition: background-color 5000s ease-in-out 0s;
          caret-color: #e5e7eb;
        }
        .dark input:-webkit-autofill::first-line {
          color: #e5e7eb;
        }
      `}</style>

      <motion.div
        className="relative w-full max-w-4xl bg-white dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-lg overflow-hidden flex flex-col md:flex-row"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Decorative images */}
        {DECOR_IMAGES.map((img, idx) => (
          <img
            key={idx}
            src={img.src}
            alt=""
            aria-hidden="true"
            className={`absolute ${img.style} pointer-events-none select-none mix-blend-multiply dark:mix-blend-screen dark:opacity-15`}
          />
        ))}

        {/* ─── Login Form (70%) ─── */}
        <div className="basis-full md:basis-[70%] p-4 sm:p-6 md:p-8 z-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-blue-800 dark:text-blue-200 mb-5">
            Log In
          </h2>

          {message && (
            <div
              className={`${
                /invalid|error|wrong|failed|not|unable/i.test(message)
                  ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 dark:border dark:border-red-800/40"
                  : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 dark:border dark:border-green-800/40"
              } p-2.5 sm:p-3 mb-4 rounded-md text-center text-sm sm:text-base`}
              role="alert"
            >
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <div>
              <label className="block text-slate-700 dark:text-slate-300 mb-1 sm:mb-2 text-sm sm:text-base">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 sm:px-4 py-2.5 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 placeholder-slate-500 outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500 transition text-sm sm:text-base"
                autoComplete="email"
                inputMode="email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="block text-slate-700 dark:text-slate-300 mb-1 sm:mb-2 text-sm sm:text-base">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="text-indigo-700 dark:text-indigo-400 text-xs sm:text-sm hover:underline"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>

              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 sm:px-4 py-2.5 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 placeholder-slate-500 outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500 transition text-sm sm:text-base"
                autoComplete="current-password"
              />
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-blue-700 dark:text-blue-400 hover:underline text-xs sm:text-sm"
              >
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className={`w-full text-white font-semibold py-2.5 rounded-lg transition text-sm sm:text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 dark:focus-visible:ring-indigo-500
                ${submitting
                  ? "bg-indigo-400 dark:bg-indigo-500 cursor-not-allowed"
                  : "bg-indigo-700 hover:bg-indigo-800 dark:bg-indigo-600 dark:hover:bg-indigo-700"}`}
            >
              {submitting ? "Sending OTP…" : "Login Now"}
            </button>
          </form>

          {/* Signup Prompt */}
          <div className="mt-6 text-center text-xs sm:text-sm text-slate-700 dark:text-slate-300">
            Don’t have an account?{" "}
            <Link to="/signup" className="text-indigo-700 dark:text-indigo-400 hover:underline">
              Sign up
            </Link>
          </div>
        </div>

        {/* ─── Signup Marketing (30%) ─── */}
        <div className="basis-full md:basis-[30%] bg-indigo-600 dark:bg-gradient-to-b dark:from-indigo-700 dark:to-indigo-900 p-5 sm:p-6 flex flex-col justify-center items-center text-white z-10">
          <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-white">
            New here?
          </h3>
          <p className="text-center mb-4 sm:mb-6 text-sm sm:text-base text-indigo-50/95">
            Create an account to start trading crypto securely and confidently.
          </p>
          <button
            onClick={() => navigate("/signup")}
            className="bg-white text-indigo-700 font-bold px-4 sm:px-6 py-1.5 sm:py-2 rounded-full hover:bg-indigo-100 transition text-sm sm:text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            Sign Up
          </button>
        </div>
      </motion.div>
    </div>
  );
}
